import type { QueryClient } from "@tanstack/react-query";

import { useToysStore } from "@storage/global/useToysStore";

import { QUERY_KEYS } from "@configs/api";

import { getToysSessionTokenQueryOptions } from "./toys";

interface IToysSessionRefresh {
	privateShowId: string;
	expiredToken: string;
	expiresAt: string;
	promise: Promise<void>;
}

const sessionRefreshes = new WeakMap<QueryClient, IToysSessionRefresh>();

/** Refreshes the toys session if the token is expired */
export const refreshExpiredToysSession = (
	queryClient: QueryClient,
	privateShowId: string,
	expiredToken: string,
	expiresAt: string,
): Promise<void> => {
	const options = getToysSessionTokenQueryOptions(privateShowId);
	const tokenQuery = queryClient.getQueryCache().find({ queryKey: options.queryKey });
	const tokenData = queryClient.getQueryData(options.queryKey);
	const isCurrentSession = () =>
		useToysStore.getState().activePrivateShowId === privateShowId &&
		queryClient.getQueryCache().find({ queryKey: options.queryKey }) === tokenQuery;

	if (!tokenQuery || !isCurrentSession() || tokenData?.token !== expiredToken || tokenData.expiresAt !== expiresAt) {
		return Promise.resolve();
	}

	// The popup and container share one renewal, including a failed attempt.
	const refresh = sessionRefreshes.get(queryClient);
	if (
		refresh?.privateShowId === privateShowId &&
		refresh.expiredToken === expiredToken &&
		refresh.expiresAt === expiresAt
	) {
		return refresh.promise;
	}

	const sessionQueryKey = [QUERY_KEYS.TOYS_SESSION, privateShowId];
	const capabilitiesQueryKey = [QUERY_KEYS.TOYS_CAPABILITIES, privateShowId];
	const promise = (async () => {
		await Promise.all([
			queryClient.cancelQueries({ queryKey: sessionQueryKey }),
			queryClient.cancelQueries({ queryKey: capabilitiesQueryKey }),
		]);

		if (!isCurrentSession()) return;

		await queryClient.fetchQuery({ ...options, staleTime: 0 });

		if (!isCurrentSession()) return;

		useToysStore.getState().setSessionId(null);
		queryClient.removeQueries({ queryKey: capabilitiesQueryKey });
		// Resetting the session keeps capabilities disabled until the fresh GET succeeds.
		await queryClient.resetQueries({ queryKey: sessionQueryKey });
	})();

	sessionRefreshes.set(queryClient, { privateShowId, expiredToken, expiresAt, promise });
	return promise;
};
